import { useEffect, useState } from 'react'
import type { Audience, LeadState, StepId } from '../types'
import { STEP_ORDER } from '../config'
import { readPersistedProgress } from './useChatRunner'

export interface ResumeState {
  step: StepId
  state: LeadState
}

/**
 * Reads persisted in-progress state once on mount and exposes a "resumable" prompt
 * for the user. If they accept, the runner picks up from the saved step.
 *
 * Hvis det gemte step ikke længere findes i den aktuelle audiences flow
 * (fx fordi brugeren tidligere kørte aeldre-flowet og nåede `claims`, men nu
 * lander på `/unge/chat`), så ignorerer vi resume og starter forfra. Det er
 * sikrere end at gendanne dem på et step som runner'en ikke kan navigere fra.
 */
export function useResume(audience: Audience): {
  resumable: ResumeState | null
  decide: (action: 'continue' | 'restart') => void
  decision: 'pending' | 'continue' | 'restart'
} {
  const [resumable, setResumable] = useState<ResumeState | null>(null)
  const [decision, setDecision] = useState<'pending' | 'continue' | 'restart'>('pending')

  useEffect(() => {
    const saved = readPersistedProgress()
    const validForAudience = saved && STEP_ORDER[audience].includes(saved.step)
    if (validForAudience) {
      setResumable(saved)
    } else {
      setDecision('restart')
    }
  }, [audience])

  const decide = (action: 'continue' | 'restart') => {
    setDecision(action)
  }

  return { resumable, decide, decision }
}
